<template>
  <div class="card tph-card">
    <div class="card-header tph-header">
      <div
        v-if="!teamId"
        class="d-flex align-items-center mb-1"
      >
        <feather-icon
          icon="MapPinIcon"
          size="16"
          class="mr-50 text-primary"
        />
        <strong>{{ $t('teamBoarding.positionHistory.title') }}</strong>
      </div>

      <div class="tph-filters">
        <div
          v-if="!teamId"
          class="tph-filter tph-filter--wide"
        >
          <label class="tph-label">{{ $t('teamBoarding.fields.team') }}</label>
          <select
            v-model="filter.team_id"
            class="form-control form-control-sm"
          >
            <option :value="null">
              {{ $t('teamBoarding.history.allTeams') }}
            </option>
            <option
              v-for="t in teams"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </div>
        <div class="tph-filter">
          <label class="tph-label">{{ $t('teamBoarding.history.fromLabel') }}</label>
          <input
            v-model="filter.date_from"
            type="date"
            class="form-control form-control-sm"
          >
        </div>
        <div class="tph-filter">
          <label class="tph-label">{{ $t('teamBoarding.history.toLabel') }}</label>
          <input
            v-model="filter.date_to"
            type="date"
            class="form-control form-control-sm"
          >
        </div>
        <div class="tph-filter tph-filter--time">
          <label class="tph-label">{{ $t('teamBoarding.positionHistory.timeFrom') }}</label>
          <input
            v-model="filter.time_from"
            type="time"
            class="form-control form-control-sm"
          >
        </div>
        <div class="tph-filter tph-filter--time">
          <label class="tph-label">{{ $t('teamBoarding.positionHistory.timeTo') }}</label>
          <input
            v-model="filter.time_to"
            type="time"
            class="form-control form-control-sm"
          >
        </div>
        <div class="tph-filter tph-filter--wide">
          <label class="tph-label">{{ $t('teamBoarding.positionHistory.currentPosition') }}</label>
          <select
            v-model="filter.position_type_id"
            class="form-control form-control-sm"
          >
            <option :value="null">
              {{ $t('teamBoarding.positionHistory.allPositions') }}
            </option>
            <option
              v-for="p in positionOptions"
              :key="p.id"
              :value="p.id"
            >
              {{ p.code }} — {{ p.name }}
            </option>
          </select>
        </div>
        <div class="tph-filter tph-filter--action">
          <button
            class="btn btn-add btn-sm"
            @click="search({ resetPage: true })"
          >
            <feather-icon
              icon="SearchIcon"
              size="13"
              class="mr-50"
            />
            {{ $t('common.search') }}
          </button>
        </div>
      </div>

      <small class="text-muted d-block mt-1">{{ $t('teamBoarding.positionHistory.timeHint') }}</small>

      <div
        v-if="error"
        class="tph-error mt-1"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="14"
          class="mr-50"
        />
        {{ error }}
      </div>
    </div>

    <div class="card-body p-0">
      <div
        v-if="!searched"
        class="tph-empty"
      >
        <feather-icon
          icon="CalendarIcon"
          size="38"
          class="mb-1"
        />
        <h5 class="mb-50">
          {{ $t('teamBoarding.history.selectPeriodTitle') }}
        </h5>
        <p class="mb-0">
          {{ $t('teamBoarding.positionHistory.selectPeriodBody') }}
        </p>
      </div>

      <div
        v-else-if="!rows.length"
        class="tph-empty"
      >
        <feather-icon
          icon="InboxIcon"
          size="38"
          class="mb-1"
        />
        <h5 class="mb-0">
          {{ $t('teamBoarding.positionHistory.empty') }}
        </h5>
      </div>

      <template v-else>
        <div class="tph-scroll">
          <table class="table table-sm tph-table mb-0">
            <thead>
              <tr>
                <th>{{ $t('teamBoarding.positionHistory.date') }}</th>
                <th v-if="!teamId">
                  {{ $t('teamBoarding.fields.team') }}
                </th>
                <th>{{ $t('teamBoarding.positionHistory.previousPosition') }}</th>
                <th>{{ $t('teamBoarding.positionHistory.currentPosition') }}</th>
                <th>{{ $t('teamBoarding.positionHistory.user') }}</th>
                <th>{{ $t('teamBoarding.positionHistory.origin') }}</th>
                <th>{{ $t('teamBoarding.positionHistory.occurrenceCol') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in rows"
                :key="r.id"
              >
                <td class="tph-when">
                  <strong>{{ timeOf(r.reported_at) }}</strong>
                  <small>{{ dateOf(r.reported_at) }}</small>
                </td>
                <td
                  v-if="!teamId"
                  class="tph-team"
                >
                  {{ r.team.name }}
                </td>
                <td class="tph-from">
                  <template v-if="r.previous_position">
                    <span
                      class="tph-chip tph-chip--muted"
                      :style="chipStyle(r.previous_position)"
                    >{{ r.previous_position.code }}</span>
                    {{ r.previous_position.name }}
                  </template>
                  <em v-else>{{ $t('teamBoarding.positionHistory.noPrevious') }}</em>
                </td>
                <td class="tph-to">
                  <span
                    class="tph-chip"
                    :style="chipStyle(r.position)"
                  >{{ r.position.code }}</span>
                  {{ r.position.name }}
                </td>
                <td>{{ r.user_name || '—' }}</td>
                <td>
                  <span
                    v-if="r.source"
                    class="tph-origin"
                    :class="`tph-origin--${r.source}`"
                  >{{ $t(`teamBoarding.positionHistory.sources.${r.source}`) }}</span>
                  <span
                    v-else
                    class="tph-none"
                    :title="$t('teamBoarding.positionHistory.sourceUnknown')"
                  >—</span>
                </td>
                <td class="tph-occ">
                  {{ r.occurrence_protocol || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="tph-pager">
          <pagination
            v-if="pagination.last_page > 1"
            v-model="pagination.current_page"
            :options="opcoesPaginacao"
            :records="pagination.total"
            :per-page="pagination.per_page"
            @paginate="search()"
          />
          <div class="d-flex align-items-center">
            <small class="text-muted mr-50">{{ $t('teamBoarding.pagination.perPage') }}</small>
            <select
              class="form-control form-control-sm tph-perpage"
              :value="pagination.per_page"
              @change="setPerPage($event.target.value)"
            >
              <option
                v-for="v in [10, 20, 50]"
                :key="v"
                :value="v"
              >
                {{ v }}
              </option>
            </select>
            <small
              v-if="pagination.last_page <= 1"
              class="text-muted ml-1"
            >
              {{ $t('teamBoarding.pagination.recordsCount', { count: pagination.total }) }}
            </small>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Pagination from 'vue-pagination-2'
import opcoesPaginacao from '@/libs/paginationOptions'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

const MAX_DAYS = 90

const isoDate = d => {
  const p = n => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export default {
  name: 'TeamPositionHistory',

  components: { Pagination },

  mixins: [ApiErrorMixin],

  props: {
    // Equipes do nó (id, name, discipline_id): filtro de equipe e origem das posições do filtro.
    teams: { type: Array, default: () => [] },
    // Equipe fixa (histórico aberto pelo card): esconde o filtro de equipe e carrega as posições da disciplina dela.
    teamId: { type: Number, default: null },
  },

  data() {
    const today = new Date()
    const from = new Date()
    from.setDate(today.getDate() - 6)

    return {
      opcoesPaginacao,
      filter: {
        team_id: null,
        date_from: isoDate(from),
        date_to: isoDate(today),
        time_from: '',
        time_to: '',
        position_type_id: null,
      },
      positionOptions: [],
      rows: [],
      pagination: {
        total: 0, per_page: 10, last_page: 1, current_page: 1,
      },
      searched: false,
      error: '',
      requestSeq: 0,
    }
  },

  watch: {
    teams: { immediate: true, handler: 'loadPositionOptions' },
  },

  created() {
    if (this.teamId) {
      this.filter.team_id = this.teamId
      this.loadTeamPositionOptions()
    }
  },

  mounted() {
    if (this.teamId) this.search({ resetPage: true })
  },

  methods: {
    // Chip na cor da posição (panel_color); sem cor, cai no estilo neutro da folha de estilo.
    chipStyle(position) {
      return position && position.color ? { background: position.color, color: '#fff' } : {}
    },

    timeOf(value) {
      return value ? new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'
    },

    dateOf(value) {
      return value ? new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
    },

    // As posições são por disciplina: junta as de todas as disciplinas das equipes do nó.
    async loadPositionOptions(list) {
      const source = Array.isArray(list) ? list : this.teams
      const disciplineIds = [...new Set(source.map(t => t.discipline_id).filter(Boolean))]
      if (!disciplineIds.length) return

      const results = await Promise.all(disciplineIds.map(id => this.$http
        .get(`${this.$store.state.api}team_position_types/for-node`, { params: { discipline_id: id, shown_in_occurrence: 0 } })
        .then(r => r.data.data || [])
        .catch(() => [])))

      const byId = new Map()
      results.flat().forEach(p => byId.set(p.id, p))
      this.positionOptions = [...byId.values()].sort((a, b) => String(a.code).localeCompare(String(b.code), 'pt-BR', { numeric: true }))
    },

    // Posições da disciplina da equipe fixa (o card não traz a lista de equipes do nó).
    async loadTeamPositionOptions() {
      try {
        const { data } = await this.$http.get(`${this.$store.state.api}team-boardings/status`)
        const team = (data.data || []).find(t => t.id === this.teamId)
        if (team) this.loadPositionOptions([team])
      } catch (e) {
        this.positionOptions = []
      }
    },

    setPerPage(value) {
      this.pagination.per_page = Number(value)
      this.search({ resetPage: true })
    },

    async search({ resetPage = false } = {}) {
      const { date_from: dFrom, date_to: dTo } = this.filter
      if (!dFrom || !dTo) {
        this.error = this.$t('teamBoarding.messages.missingDateRange')

        return
      }

      const from = new Date(`${dFrom}T00:00:00`)
      const to = new Date(`${dTo}T00:00:00`)
      if (from > to) {
        this.error = this.$t('teamBoarding.messages.invalidDateRange')

        return
      }

      const days = Math.round((to - from) / 86400000) + 1
      if (days > MAX_DAYS) {
        this.error = this.$t('teamBoarding.messages.rangeTooLong', { max: MAX_DAYS, days })

        return
      }

      // Janela de horário (todos os dias do período): as duas pontas juntas, ou nenhuma.
      if (!!this.filter.time_from !== !!this.filter.time_to) {
        this.error = this.$t('teamBoarding.positionHistory.timeBoth')

        return
      }

      this.error = ''
      if (resetPage) this.pagination.current_page = 1

      this.requestSeq += 1
      const seq = this.requestSeq

      this.$loading(true)
      try {
        const params = {
          date_from: dFrom,
          date_to: dTo,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        }
        if (this.filter.team_id) params.team_id = this.filter.team_id
        if (this.filter.time_from && this.filter.time_to) {
          params.time_from = this.filter.time_from
          params.time_to = this.filter.time_to
        }
        if (this.filter.position_type_id) params.position_type_id = this.filter.position_type_id

        const { data } = await this.$http.get(`${this.$store.state.api}team_positions/log`, { params })
        if (seq !== this.requestSeq) return

        const payload = data.data
        this.rows = payload.data || []
        this.pagination.total = payload.total || 0
        this.pagination.per_page = payload.per_page || this.pagination.per_page
        this.pagination.last_page = payload.last_page || 1
        this.pagination.current_page = payload.current_page || 1
        this.searched = true
      } catch (e) {
        if (seq !== this.requestSeq) return
        this.error = this.apiErrorText(e)
      } finally {
        if (seq === this.requestSeq) this.$loading(false)
      }
    },
  },
}
</script>

<style scoped>
.tph-card {
  background: var(--tb-surface, #fff);
  border: 1px solid var(--tb-border, #ebe9f1);
  border-radius: 6px;
}
.tph-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 12px;
  background: transparent;
  border-bottom: 1px solid var(--tb-border, #ebe9f1);
}
.tph-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 12px;
}
.tph-filter { min-width: 140px; }
.tph-filter--time { min-width: 110px; }
.tph-filter--wide { min-width: 220px; flex: 1 1 220px; }
.tph-label {
  display: block;
  margin-bottom: 3px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--tb-text-muted, #6e6b7b);
}
.tph-error {
  display: flex;
  align-items: center;
  color: #ea5455;
  font-size: 0.82rem;
}
.tph-empty {
  padding: 48px 16px;
  text-align: center;
  color: var(--tb-text-muted, #6e6b7b);
}
.tph-scroll { overflow-x: auto; }
.tph-table thead th {
  padding: 9px 12px;
  background: var(--tb-surface-alt, rgba(115, 103, 240, 0.04));
  border-top: none;
  border-bottom: 1px solid var(--tb-border, #ebe9f1);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--tb-text-muted, #6e6b7b);
}
.tph-table tbody td {
  padding: 8px 12px;
  vertical-align: middle;
  border-top: 1px solid var(--tb-border, #ebe9f1);
  font-size: 0.82rem;
}
.tph-table tbody tr:hover td { background: var(--tb-surface-alt, rgba(115, 103, 240, 0.04)); }
.tph-when { white-space: nowrap; line-height: 1.25; font-variant-numeric: tabular-nums; }
.tph-when small { display: block; color: var(--tb-text-muted, #6e6b7b); font-size: 0.7rem; }
.tph-from { color: var(--tb-text-muted, #6e6b7b); }
.tph-from em { font-size: 0.78rem; }
.tph-to { font-weight: 600; }
.tph-team { font-weight: 600; }
.tph-pager {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 12px 12px;
  border-top: 1px solid var(--tb-border, #ebe9f1);
}
.tph-pager >>> .VuePagination { text-align: center; }
.tph-pager >>> .VuePagination__pagination { justify-content: center; margin: 0; }
.tph-pager >>> .VuePagination__count { text-align: center; }
.tph-chip {
  display: inline-block;
  min-width: 34px;
  margin-right: 4px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(115, 103, 240, 0.14);
  color: #5e50ee;
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
}
.tph-chip--muted { opacity: 0.75; }
.tph-origin {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(130, 134, 139, 0.12);
  color: #6e6b7b;
  font-size: 0.74rem;
  white-space: nowrap;
}
.tph-origin--despacho { background: rgba(37, 99, 235, 0.1); color: #2563eb; }
.tph-origin--embarque { background: rgba(40, 199, 111, 0.12); color: #1f9d57; }
.tph-origin--app { background: rgba(255, 159, 67, 0.15); color: #d9740f; }
.tph-none { color: var(--tb-text-muted, #a5a3ae); cursor: help; }
.tph-occ { font-variant-numeric: tabular-nums; color: var(--tb-text-muted, #6e6b7b); white-space: nowrap; }
.tph-origin--retencao { background: rgba(234, 84, 85, 0.12); color: #c93c3d; }
.tph-origin--cadastro { background: rgba(130, 134, 139, 0.15); color: #5d6168; }
.tph-perpage { width: 70px; }
</style>
